import { computed, reactive, ref, watch } from 'vue';
import axios from 'axios';
import { authState } from '@/utils/auth';
import {
  DEFAULT_CHART_DISPLAY_SETTINGS,
  normalizeChartDisplaySettings,
  readCachedChartDisplaySettings,
  writeCachedChartDisplaySettings,
} from '@/utils/chartDisplaySettings';

const settingsState = reactive({
  ...DEFAULT_CHART_DISPLAY_SETTINGS,
});

const loaded = ref(false);
const loading = ref(false);
const saving = ref(false);
const saveError = ref('');
const panelOpen = ref(false);

let saveTimer = null;
let loadPromise = null;

function applyLocal(next) {
  const normalized = normalizeChartDisplaySettings(next);
  Object.assign(settingsState, normalized);
  writeCachedChartDisplaySettings(normalized);
  return normalized;
}

async function persistToServer(normalized) {
  if (!authState.isAuthenticated) {
    return;
  }
  saving.value = true;
  saveError.value = '';
  try {
    await axios.patch('/api/v1/profile/me', {
      chart_display_settings: normalized,
    });
  } catch (error) {
    const detail = error?.response?.data?.detail;
    saveError.value = typeof detail === 'string'
      ? detail
      : 'Could not save display settings.';
  } finally {
    saving.value = false;
  }
}

function scheduleSave() {
  if (saveTimer) {
    clearTimeout(saveTimer);
  }
  saveTimer = setTimeout(() => {
    persistToServer({ ...settingsState });
  }, 400);
}

export async function loadChartDisplaySettings({ force = false } = {}) {
  if (loadPromise && !force) {
    return loadPromise;
  }

  loadPromise = (async () => {
    loading.value = true;
    try {
      const cached = readCachedChartDisplaySettings();
      if (cached) {
        Object.assign(settingsState, cached);
      }

      if (!authState.isAuthenticated) {
        if (!cached) {
          applyLocal(DEFAULT_CHART_DISPLAY_SETTINGS);
        }
        return { ...settingsState };
      }

      const response = await axios.get('/api/v1/profile/me');
      const fromServer = response.data?.chart_display_settings;
      return applyLocal(fromServer || DEFAULT_CHART_DISPLAY_SETTINGS);
    } catch {
      if (!readCachedChartDisplaySettings()) {
        applyLocal(DEFAULT_CHART_DISPLAY_SETTINGS);
      }
      return { ...settingsState };
    } finally {
      loading.value = false;
      loaded.value = true;
    }
  })();

  try {
    return await loadPromise;
  } finally {
    loadPromise = null;
  }
}

export function useChartDisplaySettings() {
  const settings = computed(() => ({ ...settingsState }));

  function updateSettings(partial) {
    applyLocal({ ...settingsState, ...partial });
    scheduleSave();
  }

  function setSetting(key, value) {
    updateSettings({ [key]: value });
  }

  function resetToDefaults() {
    applyLocal(DEFAULT_CHART_DISPLAY_SETTINGS);
    scheduleSave();
  }

  function openPanel() {
    panelOpen.value = true;
  }

  function closePanel() {
    panelOpen.value = false;
  }

  function togglePanel() {
    panelOpen.value = !panelOpen.value;
  }

  // Keep cache in sync if auth flips after first load.
  watch(
    () => authState.isAuthenticated,
    (isAuthenticated, wasAuthenticated) => {
      if (isAuthenticated && !wasAuthenticated) {
        loadChartDisplaySettings({ force: true });
      }
    },
  );

  return {
    settings,
    settingsState,
    loaded,
    loading,
    saving,
    saveError,
    panelOpen,
    updateSettings,
    setSetting,
    resetToDefaults,
    openPanel,
    closePanel,
    togglePanel,
    loadChartDisplaySettings,
  };
}
